if (!customElements.get('pdp-judgeme-reviews')) {
  customElements.define('pdp-judgeme-reviews', class extends HTMLElement {
    connectedCallback() {
      this.token = (this.dataset.publicToken || '').trim();
      this.shop = this.dataset.shopDomain || '';
      this.productId = this.dataset.productId || '';
      this.handle = this.dataset.productHandle || '';
      this.pageSize = Math.max(1, Number(this.dataset.reviewsPerPage || 3));
      this.fetchSize = Math.max(20, this.pageSize);
      this.verifiedLabel = this.dataset.verifiedLabel || 'Verified buyer';
      this.loadLabel = this.dataset.loadMoreLabel || 'Load More';
      this.fewerLabel = this.dataset.showFewerLabel || 'Show Fewer Reviews';
      this.emptyText = this.dataset.emptyText || 'No reviews have been published for this product yet.';
      this.errorText = this.dataset.errorText || 'Reviews could not be loaded. Please try again.';
      this.showMedia = this.dataset.showMedia !== 'false';
      this.fullReviewCount = Math.max(0, Number(this.dataset.fullReviewCount || 0));
      this.fullAverageRating = Number(this.dataset.fullAverageRating || 0);
      this.grid = this.querySelector('[data-review-grid]');
      this.status = this.querySelector('[data-review-status]');
      this.moreButton = this.querySelector('[data-load-more]');
      this.fewerButton = this.querySelector('[data-show-fewer]');
      this.writeButton = this.querySelector('[data-write-review]');
      this.native = this.querySelector('[data-native-source]');
      this.summary = this.querySelector('[data-review-summary]');
      this.averageNode = this.querySelector('[data-average-rating]');
      this.countNode = this.querySelector('[data-review-count]');
      this.reviews = [];
      this.visibleCount = this.pageSize;
      this.nextUrl = '';
      this.loadingMore = false;
      this.destroyed = false;

      if (!this.fewerButton && this.moreButton) {
        this.fewerButton = this.moreButton.cloneNode(false);
        this.fewerButton.removeAttribute('data-load-more');
        this.fewerButton.setAttribute('data-show-fewer', '');
        this.fewerButton.textContent = this.fewerLabel;
        this.fewerButton.hidden = true;
        this.moreButton.insertAdjacentElement('afterend', this.fewerButton);
      }

      this.moreButton?.addEventListener('click', () => this.loadMoreReviews());
      this.fewerButton?.addEventListener('click', () => this.showFewerReviews());
      this.writeButton?.addEventListener('click', () => this.openForm());
      this.init();
    }

    disconnectedCallback() {
      this.destroyed = true;
    }

    apiUrl(page) {
      const url = new URL('https://judge.me/api/v1/widgets/product_review');
      url.search = new URLSearchParams({
        shop_domain: this.shop,
        api_token: this.token,
        external_id: this.productId,
        handle: this.handle,
        page: String(page),
        per_page: String(this.fetchSize),
        json_request: 'true'
      });
      return url.toString();
    }

    async init() {
      this.setLoading(true, this.dataset.loadingText || 'Loading verified reviews…');

      try {
        if (this.token) {
          const first = await this.fetchPage(this.apiUrl(1));
          this.reviews = this.unique(first.reviews);
          this.nextUrl = first.next || (first.hasMore || first.reviews.length >= this.fetchSize ? this.apiUrl(2) : '');
          this.paintInitial();
          this.queueBackgroundFetch();
        } else {
          this.reviews = await this.readNativeReviews();
          this.paintInitial();
        }
      } catch (error) {
        console.warn('[Judge.me progressive reviews]', error);
        try {
          this.reviews = await this.readNativeReviews();
          this.paintInitial();
        } catch (nativeError) {
          console.warn('[Judge.me native fallback]', nativeError);
          this.reviews = [];
          this.render();
          this.updateButton();
          this.setLoading(false);
          this.showStatus(this.errorText);
        }
      }
    }

    paintInitial() {
      this.visibleCount = Math.min(this.pageSize, this.reviews.length);
      this.render();
      this.updateSummary();
      this.updateButton();
      this.setLoading(false);
    }

    queueBackgroundFetch() {
      if (!this.nextUrl) return;
      const run = () => this.fetchRemaining().catch((error) => console.warn('[Judge.me background reviews]', error));
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 1800 });
      } else {
        window.setTimeout(run, 350);
      }
    }

    async fetchRemaining() {
      if (this.loadingMore || !this.nextUrl || this.destroyed) return;
      this.loadingMore = true;
      const seen = new Set(this.reviews.map((review) => this.reviewKey(review)));
      let nextUrl = this.nextUrl;
      let guard = 0;

      while (nextUrl && guard < 12 && !this.destroyed) {
        guard += 1;
        const result = await this.fetchPage(nextUrl);
        let added = 0;

        result.reviews.forEach((review) => {
          const key = this.reviewKey(review);
          if (!key || seen.has(key)) return;
          seen.add(key);
          this.reviews.push(review);
          added += 1;
        });

        if (!added) break;
        nextUrl = result.next || (result.hasMore || result.reviews.length >= this.fetchSize ? this.apiUrl(guard + 1) : '');
        await new Promise((resolve) => window.setTimeout(resolve, 0));
      }

      this.nextUrl = '';
      this.loadingMore = false;
      this.updateSummary();
      this.updateButton();
    }

    async fetchPage(url) {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        priority: 'low'
      });
      if (!response.ok) throw new Error(`Judge.me ${response.status}`);
      return this.parse(await response.text(), url);
    }

    parse(text, baseUrl) {
      let payload;
      try {
        payload = JSON.parse(text);
      } catch (error) {
        return this.parseHtml(text, baseUrl);
      }

      const rawReviews = [payload?.reviews, payload?.data?.reviews, payload?.review_widget?.reviews, payload?.widget?.reviews].find(Array.isArray);
      if (!rawReviews) {
        const html = payload.widget || payload.html || payload.data?.widget || payload.data?.html || payload.review_widget || '';
        return this.parseHtml(html, baseUrl);
      }

      const meta = payload.meta || payload.pagination || payload.data?.meta || {};
      const links = payload.links || payload.data?.links || {};
      const currentPage = Number(meta.current_page || meta.page || payload.page || 1);
      const totalPages = Number(meta.total_pages || meta.pages || payload.total_pages || 0);
      const next = links.next || meta.next_page_url || payload.next_page_url || '';

      return {
        reviews: rawReviews.map((review, index) => this.normalize(review, index)).filter((review) => review.body || review.title),
        next: this.absolute(next, baseUrl),
        hasMore: totalPages ? currentPage < totalPages : false
      };
    }

    normalize(review, index) {
      const reviewer = review.reviewer || {};
      const pictures = review.pictures || review.photos || review.images || [];
      const picture = pictures.find((item) => !item?.hidden) || pictures[0];
      return {
        id: String(review.uuid || review.id || review.review_uuid || `${review.reviewer_name || reviewer.name || 'review'}-${review.created_at || index}`),
        rating: this.clampRating(review.rating || review.score || review.stars),
        title: this.clean(review.title || review.review_title),
        body: this.clean(review.body || review.content || review.review_body || review.text),
        author: this.clean(review.reviewer_name || review.name || reviewer.name || 'Anonymous'),
        verified: Boolean(review.verified || review.verified_buyer || review.is_verified || review.buyer_verified || reviewer.verified),
        date: review.created_at || review.reviewed_at || review.date || '',
        product: this.clean(review.product_title || review.product?.title),
        image: typeof picture === 'string' ? picture : (picture?.urls?.original || picture?.url || picture?.src || '')
      };
    }

    parseHtml(html, baseUrl = '') {
      const root = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
      const reviews = [...root.querySelectorAll('.jdgm-rev')].map((element, index) => {
        const rating = element.querySelector('[data-score], .jdgm-rev__rating');
        const image = element.querySelector('.jdgm-rev__pic-link img, .jdgm-rev__pics img');
        return {
          id: String(element.dataset.reviewId || element.dataset.id || element.id || `native-${index}-${element.textContent.length}`),
          rating: this.clampRating(rating?.dataset.score || rating?.getAttribute('data-rating')),
          title: this.text(element, '.jdgm-rev__title'),
          body: this.text(element, '.jdgm-rev__body'),
          author: this.text(element, '.jdgm-rev__author') || 'Anonymous',
          verified: Boolean(element.querySelector('.jdgm-rev__buyer-badge, [class*=verified]')),
          date: this.text(element, '.jdgm-rev__timestamp'),
          product: this.text(element, '.jdgm-rev__prod-link'),
          image: image?.currentSrc || image?.src || ''
        };
      }).filter((review) => review.body || review.title);
      const nextElement = root.querySelector('.jdgm-paginate__next-page:not(.jdgm-disabled), [rel=next]');
      return { reviews, next: this.absolute(nextElement?.href || nextElement?.dataset?.url || nextElement?.getAttribute('data-href') || '', baseUrl), hasMore: Boolean(nextElement) };
    }

    async readNativeReviews() {
      if (!this.native) return [];
      const read = () => this.parseHtml(this.native).reviews;
      const immediate = read();
      if (immediate.length) return this.unique(immediate);
      return new Promise((resolve) => {
        const observer = new MutationObserver(() => {
          const reviews = read();
          if (!reviews.length) return;
          observer.disconnect();
          resolve(this.unique(reviews));
        });
        observer.observe(this.native, { childList: true, subtree: true, attributes: true });
        window.setTimeout(() => { observer.disconnect(); resolve(this.unique(read())); }, 2500);
      });
    }

    loadMoreReviews() {
      if (!this.reviews.length || this.visibleCount >= this.reviews.length) return;
      this.visibleCount = Math.min(this.visibleCount + this.pageSize, this.reviews.length);
      this.render();
      this.updateButton();
    }

    showFewerReviews() {
      if (!this.reviews.length || this.visibleCount <= this.pageSize) return;
      this.visibleCount = Math.min(this.pageSize, this.reviews.length);
      this.render();
      this.updateButton();
    }

    render() {
      if (!this.grid) return;
      const fragment = document.createDocumentFragment();
      this.reviews.slice(0, this.visibleCount).forEach((review) => fragment.append(this.card(review)));
      this.grid.replaceChildren(fragment);
      this.grid.hidden = !this.reviews.length;
      this.grid.setAttribute('aria-busy', 'false');
      this.showStatus(this.reviews.length ? '' : this.emptyText);
    }

    updateSummary() {
      const loadedCount = this.reviews.length;
      const count = this.fullReviewCount > 0 ? this.fullReviewCount : loadedCount;
      if (!count) {
        if (this.summary) this.summary.hidden = true;
        return;
      }

      const loadedAverage = loadedCount
        ? this.reviews.reduce((sum, review) => sum + review.rating, 0) / loadedCount
        : 0;
      const average = this.fullAverageRating > 0 ? this.fullAverageRating : loadedAverage;

      if (this.averageNode) this.averageNode.textContent = Number(average || 0).toFixed(1);
      if (this.countNode) this.countNode.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-GB').format(count);
      if (this.summary) this.summary.hidden = false;
    }

    updateButton() {
      if (!this.moreButton) return;
      this.moreButton.hidden = this.reviews.length <= this.visibleCount;
      this.moreButton.textContent = this.loadLabel;

      if (this.fewerButton) {
        this.fewerButton.hidden = this.visibleCount <= this.pageSize;
        this.fewerButton.textContent = this.fewerLabel;
      }
    }

    card(review) {
      const article = document.createElement('article');
      article.className = 'pdp-judgeme-reviews__card';
      const header = document.createElement('div');
      header.className = 'pdp-judgeme-reviews__card-header';
      const stars = document.createElement('span');
      stars.className = 'pdp-judgeme-reviews__card-stars';
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
      header.append(stars);
      if (review.verified) {
        const badge = document.createElement('span');
        badge.className = 'pdp-judgeme-reviews__verified';
        badge.textContent = this.verifiedLabel;
        header.append(badge);
      }
      article.append(header);
      if (review.title) {
        const title = document.createElement('h3');
        title.className = 'pdp-judgeme-reviews__card-title';
        title.textContent = review.title;
        article.append(title);
      }
      if (review.body) {
        const body = document.createElement('p');
        body.className = 'pdp-judgeme-reviews__card-body';
        body.textContent = `“${review.body}”`;
        article.append(body);
      }
      if (this.showMedia && review.image) {
        const link = document.createElement('a');
        link.className = 'pdp-judgeme-reviews__media';
        link.href = review.image;
        link.target = '_blank';
        link.rel = 'noopener';
        const image = document.createElement('img');
        image.src = review.image;
        image.alt = `Review photo from ${review.author}`;
        image.loading = 'lazy';
        image.decoding = 'async';
        link.append(image);
        article.append(link);
      }
      const footer = document.createElement('footer');
      footer.className = 'pdp-judgeme-reviews__card-footer';
      const reviewer = document.createElement('div');
      reviewer.className = 'pdp-judgeme-reviews__reviewer';
      const name = document.createElement('strong');
      name.textContent = review.author;
      reviewer.append(name);
      if (review.product) {
        const product = document.createElement('span');
        product.textContent = review.product;
        reviewer.append(product);
      }
      footer.append(reviewer);
      if (review.date) {
        const date = document.createElement('time');
        date.className = 'pdp-judgeme-reviews__date';
        date.textContent = this.formatDate(review.date);
        footer.append(date);
      }
      article.append(footer);
      return article;
    }

    async openForm() {
      if (!this.native) return;
      this.classList.add('is-native-form-open');
      this.native.setAttribute('aria-hidden', 'false');
      await new Promise((resolve) => window.setTimeout(resolve, 100));
      this.native.querySelector('.jdgm-write-rev-link, [data-write-review], button[class*=write]')?.click();
    }

    setLoading(active, message = '') {
      this.grid?.setAttribute('aria-busy', String(active));
      if (active) this.showStatus(message);
      else if (this.reviews.length) this.showStatus('');
    }

    showStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
    }

    unique(reviews) {
      const seen = new Set();
      return reviews.filter((review) => {
        const key = this.reviewKey(review);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    reviewKey(review) {
      return this.clean(`${review.id || ''}|${review.author || ''}|${review.body || review.title || ''}|${review.date || ''}`).toLowerCase();
    }

    absolute(value, baseUrl) {
      if (!value) return '';
      try { return new URL(value, baseUrl || location.href).toString(); } catch (error) { return ''; }
    }

    text(root, selector) { return this.clean(root.querySelector(selector)?.textContent); }
    clean(value) { return String(value || '').replace(/\s+/g, ' ').trim(); }
    clampRating(value) { return Math.max(1, Math.min(5, Math.round(Number(value) || 5))); }
    formatDate(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? this.clean(value) : new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', { month: 'short', year: 'numeric' }).format(date);
    }
  });
}